import { motion } from 'framer-motion';
import { useEffect, useState } from 'react';
import { Button } from '../Button';
import { Card, Ornament } from '../Card';
import { PillarIcon } from '../../lib/icons';
import { HeroVideo } from '../HeroVideo';
import type { HomeProps } from './types';

export function ClassicHome(p: HomeProps) {
  const videoUrl = p.heroVideoEnabled && p.heroVideoUrl ? p.heroVideoUrl : undefined;
  const v = !!videoUrl; // shorthand for video-active styles
  return (
    <>
      <div className={`relative pt-10 ${v ? 'pb-32 -mx-5 px-5 overflow-hidden' : 'pb-8'}`}>
        {videoUrl ? <HeroVideo url={videoUrl} /> : <div className="absolute inset-x-0 top-0 h-52 najdi-pattern pointer-events-none" aria-hidden />}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="relative z-[2] text-center space-y-5"
        >
          <div className="flex justify-center">
            <span className={`inline-flex items-center gap-2 rounded-full px-4 py-1.5 text-[11px] font-bold tracking-wider shadow-soft ${v ? 'bg-white/10 backdrop-blur-md border border-white/20 text-gold-soft' : 'bg-oud-800 text-gold-soft'}`}>
              <span className="relative flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full rounded-full bg-gold opacity-75 animate-ping" />
                <span className="relative inline-flex h-2 w-2 rounded-full bg-gold" />
              </span>
              {p.instantBadge}
            </span>
          </div>
          <h1 className={`text-[2.75rem] md:text-6xl font-black leading-[1.05] whitespace-pre-line tracking-tight ${v ? 'text-white drop-shadow-md' : 'text-oud-900'}`}>
            {p.headline}
          </h1>
          <p className={`leading-relaxed px-2 text-[15px] ${v ? 'text-white/85 drop-shadow-sm' : 'text-oud-700'}`}>
            {p.subheadline}
          </p>
        </motion.div>

        {v ? (
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15, duration: 0.5 }} className="relative z-[2] space-y-3 mt-5 max-w-xs mx-auto">
            <button type="button" onClick={p.onCamera}
              className="cta-primary w-full backdrop-blur-lg text-white py-4 rounded-2xl text-lg font-bold active:scale-[0.98]">
              {p.ctaLabel}
            </button>
            <button type="button" onClick={p.onGallery}
              className="w-full text-sm font-bold py-2 text-white/70 hover:text-white transition-colors">
              {p.ctaUploadLabel}
            </button>
          </motion.div>
        ) : null}
      </div>

      {v ? null : (
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15, duration: 0.5 }} className="space-y-3">
          <Button size="lg" fullWidth onClick={p.onCamera}>
            {p.ctaLabel}
          </Button>
          <Button variant="secondary" size="md" fullWidth onClick={p.onGallery}>
            {p.ctaUploadLabel}
          </Button>
        </motion.div>
      )}

      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3, duration: 0.5 }} className="mt-8 grid grid-cols-2 gap-2.5">
        {p.pillars.map((pl) => (
          <div key={pl.label} className="rounded-2xl border border-oud-200 px-3 py-3 flex items-center gap-2.5">
            <div className="flex-none w-9 h-9 rounded-full bg-gold/15 text-gold-deep flex items-center justify-center">
              <PillarIcon name={pl.icon} />
            </div>
            <span className="text-[12.5px] font-bold text-oud-800 leading-tight">{pl.label}</span>
          </div>
        ))}
      </motion.div>

      <section className="mt-14">
        <div className="flex items-center gap-3 mb-4">
          <Ornament className="flex-1" />
          <h2 className="text-sm font-bold text-oud-700 tracking-widest">{p.stepsTitle}</h2>
          <Ornament className="flex-1" />
        </div>
        <div className="space-y-3">
          {p.steps.map((step, i) => (
            <div key={step} className="flex gap-4 items-start bg-gradient-to-br from-oud-800 to-oud-900 rounded-2xl p-4 shadow-soft">
              <div className="flex-none w-8 h-8 rounded-xl bg-gold/20 text-gold-soft flex items-center justify-center font-black text-sm">{i + 1}</div>
              <p className="text-sand leading-relaxed pt-1">{step}</p>
            </div>
          ))}
        </div>
      </section>

      <ExternalLink {...p} />
      <WhatsAppSection {...p} />
    </>
  );
}

function ExternalLink(p: HomeProps) {
  if (!p.websiteUrl) return null;
  return (
    <section className="mt-10">
      <a href={p.websiteUrl} target="_blank" rel="noreferrer noopener"
        className="group block relative overflow-hidden rounded-3xl bg-gradient-to-br from-oud-800 to-oud-900 p-6 shadow-quote hover:shadow-quote transition-all">
        <div className="absolute inset-0 najdi-pattern opacity-20 pointer-events-none" aria-hidden />
        <div className="relative flex items-center justify-between gap-4">
          <div>
            <p className="text-[10px] tracking-[0.3em] text-gold-soft/70 font-bold uppercase">{p.brandName}</p>
            <p className="text-lg font-black text-sand mt-1">{p.websiteLabel}</p>
          </div>
          <div className="flex-none w-10 h-10 rounded-full bg-gold/20 border border-gold-soft/30 flex items-center justify-center group-hover:bg-gold/30 transition-colors">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4 text-gold-soft" aria-hidden>
              <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" /><path d="M15 3h6v6" /><path d="M10 14L21 3" />
            </svg>
          </div>
        </div>
      </a>
    </section>
  );
}

function WhatsAppSection(p: HomeProps) {
  if (!p.showWhatsapp || !p.waNumber) return null;
  return null;
}

export function WhatsAppFab({ waNumber }: { waNumber: string }) {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    function checkVisibility() {
      const viewportBottom = window.scrollY + window.innerHeight;
      const documentBottom = document.documentElement.scrollHeight;
      const nearPageEnd = documentBottom - viewportBottom <= 280;
      setIsVisible(nearPageEnd);
    }

    checkVisibility();
    window.addEventListener('scroll', checkVisibility, { passive: true });
    window.addEventListener('resize', checkVisibility);

    return () => {
      window.removeEventListener('scroll', checkVisibility);
      window.removeEventListener('resize', checkVisibility);
    };
  }, []);

  if (!isVisible) return null;

  return (
    <a
      href={`https://wa.me/${waNumber}`}
      target="_blank"
      rel="noreferrer noopener"
      className="fixed bottom-6 right-4 z-30 w-14 h-14 rounded-full bg-[#25D366] text-white flex items-center justify-center shadow-quote hover:scale-105 active:scale-95 transition-transform"
      aria-label="تواصل عبر واتساب"
    >
      <svg viewBox="0 0 32 32" fill="currentColor" className="w-7 h-7" aria-hidden>
        <path d="M16.002 3C8.82 3 3 8.82 3 16c0 2.29.6 4.52 1.74 6.48L3 29l6.7-1.76A12.94 12.94 0 0 0 16 29C23.18 29 29 23.18 29 16S23.18 3 16.002 3Zm0 23.62c-1.98 0-3.92-.53-5.62-1.54l-.4-.24-3.97 1.04 1.06-3.87-.26-.41A10.6 10.6 0 0 1 5.38 16c0-5.86 4.77-10.62 10.63-10.62S26.62 10.14 26.62 16 21.86 26.62 16 26.62Zm6.1-7.95c-.33-.17-1.96-.97-2.26-1.08-.3-.11-.53-.17-.75.17-.22.33-.86 1.08-1.06 1.31-.2.22-.39.25-.72.08-.33-.17-1.4-.52-2.67-1.66-.99-.88-1.65-1.97-1.85-2.3-.2-.33-.02-.51.14-.67.15-.15.33-.39.5-.58.17-.2.22-.33.33-.55.11-.22.06-.42-.03-.58-.08-.17-.75-1.8-1.03-2.47-.27-.65-.54-.56-.75-.57l-.64-.01c-.22 0-.58.08-.88.42-.3.33-1.16 1.13-1.16 2.76 0 1.63 1.19 3.2 1.36 3.42.17.22 2.35 3.58 5.7 5.02.8.35 1.42.55 1.9.71.8.25 1.53.22 2.1.13.64-.09 1.96-.8 2.24-1.58.28-.77.28-1.43.2-1.57-.08-.13-.3-.22-.63-.38Z"/>
      </svg>
    </a>
  );
}
